import { useState } from "react";

import { captureException, captureMessage } from "@sentry/react-native";
import { Linking, StyleProp, ViewStyle } from "react-native";
import { Button } from "react-native-paper";

import ActionDialog from "./ActionDialog";
import useSnackbar from "../../../../contexts/SnackbarContext";
import useTransaction from "../../contexts/TransactionContext";
import useTransactionDetailsAPI from "../../contexts/TransactionDetailsAPIContext";
import { Action } from "../../types";

type Props = {
  action?: Action;
  style?: StyleProp<ViewStyle>;
};

export default function ActionButton({ action, style }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  const { fetchTransaction } = useTransaction();
  const {
    cancelPayment,
    markAsReadyForPickup,
    markAsDelivered,
    markAsPaid,
    copyToClipboard,
    shareMessage,
    resendReceipt,
    resendRefundReceipt,
    resendPaymentInstructions,
  } = useTransactionDetailsAPI();

  const { showMessage } = useSnackbar();

  if (!action || !action.enabled) {
    return null;
  }

  const onButtonPress = () => {
    if (action.dialog.enabled) {
      setIsDialogVisible(true);
    } else {
      handleAction(action);
    }
  };

  const handleAction = async (action: Action) => {
    const { name, params, snackbarText } = action;

    // some actions may wish to not show snackbar unless conditions are met
    let showSnackbar = true;

    try {
      setIsDialogVisible(false);
      setIsLoading(true);

      switch (name) {
        case "cancelPayment":
        case "cancelSubscription":
          await cancelPayment(params);
          await fetchTransaction();
          break;

        case "markAsReadyForPickup":
          await markAsReadyForPickup(params);
          await sleep(5000);
          await fetchTransaction();
          break;

        case "markAsDelivered":
          await markAsDelivered(params);
          await fetchTransaction();
          break;

        case "markAsPaid":
          await markAsPaid(params);
          await fetchTransaction();
          break;

        case "copyFulfillmentDetails":
          await copyToClipboard(params.fulfillmentDetails);
          break;

        case "resendPaymentRequestUrl": {
          const success = shareMessage(params);
          if (!success) {
            showSnackbar = false;
          }
          break;
        }

        case "resendReceipt":
          await resendReceipt(params);
          break;

        case "resendRefundReceipt":
          await resendRefundReceipt(params);
          break;

        case "resendPaymentInstructions":
          await resendPaymentInstructions(params);
          break;

        case "openUrl":
          await Linking.openURL(params.url);
          break;

        default:
          captureMessage(`Invalid transaction details action: ${name}`, {
            extra: { action },
          });
          break;
      }
      if (showSnackbar && snackbarText) {
        let msg = snackbarText;

        if (action.dialog.enabled && action.dialog.input.enabled) {
          //@ts-expect-error TODO
          const edittableParam = action.params[action.dialog.input.param];
          msg = snackbarText.replace(".", ` a ${edittableParam}.`);
        }

        showMessage(msg);
      }
    } catch (e) {
      captureException(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ActionDialog
        visible={isDialogVisible}
        action={action}
        dismissDialog={() => setIsDialogVisible(false)}
        confirmAction={handleAction}
      />
      <Button
        mode="contained-tonal"
        onPress={onButtonPress}
        loading={isLoading}
        style={style}
      >
        {action.label}
      </Button>
    </>
  );
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
